/**
 * Mutating tools (docs/agent.md §3). Each is a resolve/apply pair — see
 * the header of catalog.ts for why.
 *
 * Conventions the model is told about and these enforce:
 *  - times the model supplies for ARCHIVE material are CONCAT seconds (whole
 *    item time, the same axis annotations use). `resolve` maps them onto a part.
 *  - times the model supplies for the TIMELINE are timeline seconds.
 *  - every failure returns an error string that says what to do instead; the
 *    loop feeds those back to the model rather than throwing (§3).
 */
import { concatRangeToPartRange } from '../../format/partRange';
import { findClip } from '../../format/ops';
import * as ops from '../../format/ops';
import { clipEnd } from '../../format/helpers';
import { MIN_PLACED_DURATION } from '../../format/partRange';
import { clipParamTime, setParamAtPath } from '../../format/params';
import type {
  Easing,
  Edit,
  Effect,
  EffectType,
  Keyframe,
  PandoraVideoSource,
  TransitionType,
} from '../../format/types';
import { TRACK_BLENDS, TRANSITION_TYPES } from '../../format/types';
import type { ApplyResult, MutateToolDef, ResolveContext, ResolveResult } from './catalog';

const EFFECT_TYPES: EffectType[] = [
  'brightness',
  'contrast',
  'saturation',
  'hue',
  'grayscale',
  'sepia',
  'invert',
  'blur',
];

/** The param name each effect type carries, for set_effect / set_keyframes. */
const EFFECT_PARAM: Record<EffectType, string> = {
  brightness: 'amount',
  contrast: 'amount',
  saturation: 'amount',
  hue: 'degrees',
  grayscale: 'amount',
  sepia: 'amount',
  invert: 'amount',
  blur: 'radius',
};

const EASINGS: Easing[] = ['linear', 'easeIn', 'easeOut', 'hold'];

function fail(error: string): ResolveResult {
  return { ok: false, error };
}

function ok(opArgs: Record<string, unknown>): ResolveResult {
  return { ok: true, opArgs };
}

/**
 * Resolve an archive item + concat range into a part-local source and range.
 * Shared by add_clip / add_sequence / add_audio — the one place concat→part
 * mapping happens, so the three cannot drift apart.
 */
async function resolveSource(
  ctx: ResolveContext,
  archive: string,
  item: string,
  concatIn: number,
  concatOut: number,
): Promise<
  { ok: true; source: PandoraVideoSource; in: number; out: number } | { ok: false; error: string }
> {
  if (concatOut < concatIn) {
    return { ok: false, error: `out (${concatOut}) is before in (${concatIn})` };
  }
  // A frame-length moment is a legitimate search result on these archives —
  // `machinedescriptions` rows can be ~0.04s — and rejecting one taught the
  // model to invent a duration instead, which is how a single frame became a
  // ten-second clip. Floor it to the shortest clip the timeline accepts and let
  // the user drag it out from there.
  if (concatOut - concatIn < MIN_PLACED_DURATION) concatOut = concatIn + MIN_PLACED_DURATION;
  let detail;
  try {
    detail = await ctx.getItem(archive, item);
  } catch (err) {
    return {
      ok: false,
      error: `could not load item "${item}" from ${archive}: ${
        err instanceof Error ? err.message : String(err)
      }. Check the item id with search_items or search_moments.`,
    };
  }
  const total = detail.duration || detail.durations.reduce((a, b) => a + b, 0);
  if (total > 0 && concatIn >= total) {
    return {
      ok: false,
      error: `in (${concatIn}s) is past the end of "${detail.title}", which is ${total.toFixed(
        1,
      )}s long. Use get_item for exact durations.`,
    };
  }
  const clamped = total > 0 ? Math.min(concatOut, total) : concatOut;
  const range = concatRangeToPartRange(detail.durations, concatIn, clamped);
  const source: PandoraVideoSource = {
    kind: 'pandora-video',
    archive,
    item,
    part: range.part,
    duration: detail.durations[range.part - 1] ?? total,
    title: detail.title,
    posterFrame: detail.posterFrame,
    url: null,
  };
  return { ok: true, source, in: range.inPoint, out: range.outPoint };
}

/**
 * Where a positional edit should land, from the model's point of view.
 *
 * `after`/`before` name a CLIP, not a time. That matters twice over:
 *  - the model already has clip ids from inspect_timeline, so "after c3" needs
 *    no arithmetic, whereas converting to seconds is a step where it can (and
 *    did) go wrong;
 *  - the op log stores the clip id, so at apply time the anchor is re-resolved
 *    against the CURRENT timeline. A proposal that said "at 8.2s" would land in
 *    the wrong place if a collaborator trimmed something first.
 *
 * Resolved inside `apply` for exactly that reason — never at resolve time.
 */
const ANCHOR_PROPS = {
  at: { type: 'number' as const, minimum: 0, description: 'timeline seconds' },
  after: { type: 'string' as const, description: 'place it directly after this clip id' },
  before: { type: 'string' as const, description: 'place it directly before this clip id' },
};

/**
 * Validate an anchor without resolving it to a time.
 *
 * Resolving to a time is what broke this twice: once because the anchor was
 * computed before the timeline changed underneath it, and once because a time
 * cannot distinguish "after clip A" from "after the clip that overlaps A".
 * The ops take the clip id and work in index space; this only checks that the
 * id exists and that the arguments are coherent.
 */
function checkAnchor(
  draft: Edit,
  args: Record<string, unknown>,
  /**
   * For move_clip: the clip being moved. An anchor on a DIFFERENT track is
   * meaningless — moveClipRipple works in index space on a single track, finds
   * no such index there, and takes its abort path, which reports success after
   * already having disturbed the source track. This is the only place that can
   * tell the difference, so it has to refuse here.
   */
  movingClipId?: string,
): { ok: true; anchored: boolean } | { ok: false; error: string } {
  const { after, before } = args;
  if (typeof after === 'string' && typeof before === 'string') {
    return { ok: false, error: 'give either after or before, not both' };
  }
  const movingTrack =
    typeof movingClipId === 'string' ? findClip(draft, movingClipId)?.track : undefined;

  for (const value of [after, before]) {
    if (typeof value !== 'string') continue;
    const loc = findClip(draft, value);
    if (!loc) {
      return {
        ok: false,
        error: `no clip "${value}" on the timeline — call inspect_timeline for current clip ids`,
      };
    }
    if (loc.track.kind !== 'video') {
      return { ok: false, error: `"${value}" is not on a video track` };
    }
    if (movingTrack && loc.track.id !== movingTrack.id) {
      return {
        ok: false,
        error:
          `"${value}" is on track ${loc.track.label} but ${movingClipId} is on ` +
          `${movingTrack.label}. Move it onto that track first (move_clip with the ` +
          `track argument), then reorder it there.`,
      };
    }
    return { ok: true, anchored: true };
  }
  return { ok: true, anchored: false };
}

function requireClip(draft: Edit, id: unknown, tool: string): string | null {
  if (typeof id !== 'string' || !findClip(draft, id)) {
    return `no clip "${String(id)}" on the timeline — call inspect_timeline for current clip ids before using ${tool}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// add_clip
// ---------------------------------------------------------------------------

export const addClipTool: MutateToolDef = {
  name: 'add_clip',
  kind: 'mutate',
  description:
    'Add one archive clip to a video track. in/out are CONCAT seconds of the whole item (the same times search_moments returns). By default it appends to the end; to place it somewhere specific use `after`/`before` with a clip id (preferred) or `at` with a timeline time. Existing clips shift right to make room.',
  schema: {
    type: 'object',
    properties: {
      archive: { type: 'string', description: 'archive key, e.g. pad.ma' },
      item: { type: 'string', description: 'item id' },
      in: { type: 'number', minimum: 0, description: 'concat seconds' },
      out: { type: 'number', minimum: 0, description: 'concat seconds' },
      ...ANCHOR_PROPS,
      track: { type: 'string', description: 'target video track id' },
    },
    required: ['archive', 'item', 'in', 'out'],
  },
  idArgPaths: ['track', 'after', 'before'],
  describeCall: (a) =>
    `Add clip ${itemOf(a)} ${fmtRange(a.in, a.out)}${a.after ? ` after ${a.after}` : ''}${
      a.before ? ` before ${a.before}` : ''
    }${a.at !== undefined ? ` at ${fmtT(a.at)}` : ''}`,
  async resolve(args, ctx) {
    const r = await resolveSource(
      ctx,
      args.archive as string,
      args.item as string,
      args.in as number,
      args.out as number,
    );
    if (!r.ok) return fail(r.error);
    return ok({
      source: r.source,
      in: r.in,
      out: r.out,
      at: args.at,
      after: args.after,
      before: args.before,
      track: args.track,
    });
  },
  apply(draft, a) {
    const clip = { source: a.source as PandoraVideoSource, in: a.in as number, out: a.out as number };
    const anchored = checkAnchor(draft, a);
    if (!anchored.ok) return { error: anchored.error };

    // Anchored to a clip: insert by POSITION, never by time. A time-based
    // insert lands after the wrong clip whenever the anchor overlaps its
    // follower for a transition.
    if (anchored.anchored) {
      return ops.insertClipsRelative(draft, {
        clips: [clip],
        after: a.after as string | undefined,
        before: a.before as string | undefined,
      });
    }
    // An explicit time still ripple-inserts. addVideoClip honours `at` only
    // past the end of the track (it is the "+Timeline" append action), so
    // routing a positional add through it silently appended instead.
    if (typeof a.at === 'number') {
      return ops.insertEditClips(draft, {
        clips: [clip],
        at: a.at,
        trackId: a.track as string | undefined,
      });
    }
    // No position asked for: append.
    return ops.addVideoClip(draft, {
      ...clip,
      targetTrack: a.track ? { video: a.track as string } : undefined,
    });
  },
};

// ---------------------------------------------------------------------------
// add_sequence
// ---------------------------------------------------------------------------

export const addSequenceTool: MutateToolDef = {
  name: 'add_sequence',
  kind: 'mutate',
  description:
    'Add several archive clips end-to-end in one call, rippling later clips right. Prefer this over repeated add_clip when assembling a sequence.',
  schema: {
    type: 'object',
    properties: {
      clips: {
        type: 'array',
        maxItems: 20,
        items: {
          type: 'object',
          properties: {
            archive: { type: 'string' },
            item: { type: 'string' },
            in: { type: 'number', minimum: 0 },
            out: { type: 'number', minimum: 0 },
          },
          required: ['archive', 'item', 'in', 'out'],
        },
      },
      ...ANCHOR_PROPS,
      track: { type: 'string' },
    },
    required: ['clips'],
  },
  idArgPaths: ['track', 'after', 'before'],
  describeCall: (a) => `Add a sequence of ${(a.clips as unknown[])?.length ?? 0} clips`,
  async resolve(args, ctx) {
    const input = args.clips as { archive: string; item: string; in: number; out: number }[];
    if (!input.length) return fail('clips must not be empty');
    const resolved = [];
    for (const c of input) {
      const r = await resolveSource(ctx, c.archive, c.item, c.in, c.out);
      if (!r.ok) return fail(`clip ${c.item}: ${r.error}`);
      resolved.push({ source: r.source, in: r.in, out: r.out });
    }
    return ok({
      clips: resolved,
      at: args.at,
      after: args.after,
      before: args.before,
      track: args.track,
    });
  },
  apply(draft, a) {
    const clips = a.clips as { source: PandoraVideoSource; in: number; out: number }[];
    const anchored = checkAnchor(draft, a);
    if (!anchored.ok) return { error: anchored.error };
    if (anchored.anchored) {
      return ops.insertClipsRelative(draft, {
        clips,
        after: a.after as string | undefined,
        before: a.before as string | undefined,
      });
    }
    return ops.insertEditClips(draft, {
      clips,
      // No anchor means "append": insertEditClips ripples from a time, so the
      // end of the track is the append point.
      at: typeof a.at === 'number' ? a.at : trackEndOf(draft, a.track as string | undefined),
      trackId: a.track as string | undefined,
    });
  },
};

/** End of the target video track (or the first one), for append semantics. */
function trackEndOf(draft: Edit, trackId: string | undefined): number {
  const track =
    (trackId && draft.tracks.find((t) => t.id === trackId && t.kind === 'video')) ||
    draft.tracks.find((t) => t.kind === 'video');
  if (!track) return 0;
  return (track.clips as { start: number }[]).reduce(
    (max, c) => Math.max(max, clipEnd(c as never)),
    0,
  );
}

// ---------------------------------------------------------------------------
// add_audio
// ---------------------------------------------------------------------------

export const addAudioTool: MutateToolDef = {
  name: 'add_audio',
  kind: 'mutate',
  description: 'Add an archive item to an audio track. in/out are CONCAT seconds.',
  schema: {
    type: 'object',
    properties: {
      archive: { type: 'string' },
      item: { type: 'string' },
      in: { type: 'number', minimum: 0 },
      out: { type: 'number', minimum: 0 },
      at: { type: 'number', minimum: 0 },
      track: { type: 'string' },
    },
    required: ['archive', 'item', 'in', 'out'],
  },
  idArgPaths: ['track'],
  describeCall: (a) => `Add audio ${itemOf(a)} ${fmtRange(a.in, a.out)}`,
  async resolve(args, ctx) {
    const r = await resolveSource(
      ctx,
      args.archive as string,
      args.item as string,
      args.in as number,
      args.out as number,
    );
    if (!r.ok) return fail(r.error);
    return ok({ source: r.source, in: r.in, out: r.out, at: args.at, track: args.track });
  },
  apply(draft, a) {
    return ops.addAudioClip(draft, {
      source: a.source as PandoraVideoSource,
      in: a.in as number,
      out: a.out as number,
      at: a.at as number | undefined,
      targetTrack: a.track ? { audio: a.track as string } : undefined,
    });
  },
};

// ---------------------------------------------------------------------------
// add_text
// ---------------------------------------------------------------------------

export const addTextTool: MutateToolDef = {
  name: 'add_text',
  kind: 'mutate',
  description: 'Add a text overlay. start/duration are timeline seconds.',
  schema: {
    type: 'object',
    properties: {
      text: { type: 'string' },
      start: { type: 'number', minimum: 0 },
      duration: { type: 'number', minimum: 0.1 },
      size: { type: 'number', minimum: 0.01, maximum: 0.5, description: 'fraction of frame height' },
      color: { type: 'string' },
      position: {
        type: 'object',
        properties: { x: { type: 'number' }, y: { type: 'number' } },
      },
      track: { type: 'string' },
    },
    required: ['text', 'start', 'duration'],
  },
  idArgPaths: ['track'],
  describeCall: (a) => `Add text ${JSON.stringify(a.text)} at ${fmtT(a.start)}`,
  async resolve(args) {
    return ok({
      text: args.text,
      start: args.start,
      duration: args.duration,
      style: {
        ...(typeof args.size === 'number' ? { fontSize: args.size } : {}),
        ...(typeof args.color === 'string' ? { color: args.color } : {}),
      },
      position: args.position,
      track: args.track,
    });
  },
  apply(draft, a) {
    return ops.addTextClip(draft, {
      text: a.text as string,
      start: a.start as number,
      duration: a.duration as number,
      style: a.style as Record<string, unknown>,
      position: a.position as { x: number; y: number } | undefined,
      targetTrack: a.track ? { overlay: a.track as string } : undefined,
    });
  },
};

// ---------------------------------------------------------------------------
// clip edits
// ---------------------------------------------------------------------------

export const trimClipTool: MutateToolDef = {
  name: 'trim_clip',
  kind: 'mutate',
  description:
    'Change a clip\'s source in/out points. Values are SOURCE seconds of the clip\'s own part — read them from inspect_timeline, which shows each clip as (src IN–OUT).',
  schema: {
    type: 'object',
    properties: {
      clip: { type: 'string' },
      in: { type: 'number', minimum: 0 },
      out: { type: 'number', minimum: 0 },
    },
    required: ['clip'],
  },
  idArgPaths: ['clip'],
  describeCall: (a) => `Trim ${a.clip} to ${fmtRange(a.in, a.out)}`,
  async resolve(args) {
    if (args.in === undefined && args.out === undefined)
      return fail('trim_clip needs at least one of in or out');
    return ok({ clip: args.clip, in: args.in, out: args.out });
  },
  apply(draft, a) {
    const err = requireClip(draft, a.clip, 'trim_clip');
    if (err) return { error: err };
    const loc = findClip(draft, a.clip as string)!;
    const clip = loc.clip as { in?: number; out?: number };
    const nextIn = (a.in as number | undefined) ?? clip.in ?? 0;
    const nextOut = (a.out as number | undefined) ?? clip.out ?? 0;
    if (nextOut <= nextIn) {
      return { error: `out (${nextOut}) must be greater than in (${nextIn})` };
    }
    const patch: Record<string, number> = {};
    if (a.in !== undefined) patch.in = a.in as number;
    if (a.out !== undefined) patch.out = a.out as number;
    return ops.updateClip(draft, { clip: a.clip as string, patch });
  },
};

export const moveClipTool: MutateToolDef = {
  name: 'move_clip',
  kind: 'mutate',
  description:
    'Reorder or reposition a clip. Use `after`/`before` with another clip id to reorder (preferred), or `start` for an exact timeline time. The clip is lifted out — closing the gap behind it — and the destination opens up to receive it, so neighbours are never overlapped. `track` moves it to another track of the same kind.',
  schema: {
    type: 'object',
    properties: {
      clip: { type: 'string' },
      start: { type: 'number', minimum: 0, description: 'exact timeline seconds' },
      after: { type: 'string', description: 'move it directly after this clip id' },
      before: { type: 'string', description: 'move it directly before this clip id' },
      track: { type: 'string' },
    },
    required: ['clip'],
  },
  idArgPaths: ['clip', 'track', 'after', 'before'],
  describeCall: (a) =>
    `Move ${a.clip}${a.after ? ` after ${a.after}` : ''}${a.before ? ` before ${a.before}` : ''}${
      a.start !== undefined ? ` to ${fmtT(a.start)}` : ''
    }${a.track ? ` onto ${a.track}` : ''}`,
  async resolve(args) {
    if (
      args.start === undefined &&
      args.track === undefined &&
      args.after === undefined &&
      args.before === undefined
    ) {
      return fail('move_clip needs one of: after, before, start, or track');
    }
    return ok({
      clip: args.clip,
      start: args.start,
      after: args.after,
      before: args.before,
      track: args.track,
    });
  },
  apply(draft, a) {
    const err = requireClip(draft, a.clip, 'move_clip');
    if (err) return { error: err };
    if (a.after === a.clip || a.before === a.clip) {
      return { error: `cannot move ${a.clip} relative to itself` };
    }
    if (a.track !== undefined) {
      const target = draft.tracks.find((t) => t.id === a.track);
      if (!target) return { error: `no track "${String(a.track)}" — use inspect_timeline` };
      ops.moveClipToTrack(draft, { clip: a.clip as string, track: a.track as string });
    }
    // Validate only: moveClipRipple resolves the POSITION itself, after the
    // clip is lifted out. Resolving here would be stale by the lifted duration.
    // Runs AFTER any track move above, so the same-track rule is evaluated
    // against where the clip actually ends up rather than where it started.
    const anchored = checkAnchor(draft, a, a.clip as string);
    if (!anchored.ok) return { error: anchored.error };
    if (a.after !== undefined || a.before !== undefined || a.start !== undefined) {
      // Ripple, never a bare `start` write: setting start directly leaves an
      // overlap that normalization turns into a crossfade nobody asked for.
      ops.moveClipRipple(draft, {
        clip: a.clip as string,
        after: a.after as string | undefined,
        before: a.before as string | undefined,
        at: a.start as number | undefined,
      });
    }
    return { created: [] };
  },
};

export const splitClipTool: MutateToolDef = {
  name: 'split_clip',
  kind: 'mutate',
  description: 'Split a clip at a timeline time, producing two clips.',
  schema: {
    type: 'object',
    properties: { clip: { type: 'string' }, at: { type: 'number', minimum: 0 } },
    required: ['clip', 'at'],
  },
  idArgPaths: ['clip'],
  describeCall: (a) => `Split ${a.clip} at ${fmtT(a.at)}`,
  async resolve(args) {
    return ok({ clip: args.clip, at: args.at });
  },
  apply(draft, a) {
    const err = requireClip(draft, a.clip, 'split_clip');
    if (err) return { error: err };
    const result = ops.splitAt(draft, { at: a.at as number, clips: [a.clip as string] });
    if (!result.created.length) {
      return {
        error: `${a.at}s is not strictly inside ${a.clip} — split at a time between the clip's start and end (see inspect_timeline)`,
      };
    }
    return result;
  },
};

export const detachAudioTool: MutateToolDef = {
  name: 'detach_audio',
  kind: 'mutate',
  description:
    "Move video clips' audio onto an audio track so it can be cut, moved and faded on its own, muting the video clips. Each detached clip lands on the first audio track free at its time (a new one is created if needed). Already-muted clips and non-video clips are skipped.",
  schema: {
    type: 'object',
    properties: {
      clips: { type: 'array', maxItems: 100, items: { type: 'string' } },
    },
    required: ['clips'],
  },
  idArgPaths: ['clips.*'],
  describeCall: (a) => `Detach audio from ${(a.clips as unknown[])?.length ?? 0} clip(s)`,
  async resolve(args) {
    if (!(args.clips as unknown[]).length) return fail('clips must not be empty');
    return ok({ clips: args.clips });
  },
  apply(draft, a) {
    const result = ops.detachAudio(draft, { clips: a.clips as string[] });
    if (!result.created.length) {
      return {
        error:
          'Nothing to detach — every id was already muted, or was not a clip on a video track (see inspect_timeline)',
      };
    }
    return result;
  },
};

export const deleteClipsTool: MutateToolDef = {
  name: 'delete_clips',
  kind: 'mutate',
  description:
    'Delete clips. With ripple:true the freed gaps are closed and later clips shift left.',
  schema: {
    type: 'object',
    properties: {
      clips: { type: 'array', maxItems: 100, items: { type: 'string' } },
      ripple: { type: 'boolean' },
    },
    required: ['clips'],
  },
  idArgPaths: ['clips.*'],
  describeCall: (a) =>
    `${a.ripple ? 'Ripple-delete' : 'Delete'} ${(a.clips as unknown[])?.length ?? 0} clip(s)`,
  async resolve(args) {
    if (!(args.clips as unknown[]).length) return fail('clips must not be empty');
    return ok({ clips: args.clips, ripple: args.ripple ?? false });
  },
  apply(draft, a) {
    const clips = a.clips as string[];
    return a.ripple
      ? ops.rippleDelete(draft, { clips })
      : ops.deleteClips(draft, { clips });
  },
};

export const setClipTool: MutateToolDef = {
  name: 'set_clip',
  kind: 'mutate',
  description:
    'Set playback properties of one clip: speed (0.25–4), volume, muted, fadeIn/fadeOut seconds, and static transform values (scale, x, y, rotate, opacity).',
  schema: {
    type: 'object',
    properties: {
      clip: { type: 'string' },
      speed: { type: 'number', minimum: 0.25, maximum: 4 },
      volume: { type: 'number', minimum: 0, maximum: 2 },
      muted: { type: 'boolean' },
      fadeIn: { type: 'number', minimum: 0 },
      fadeOut: { type: 'number', minimum: 0 },
      transform: {
        type: 'object',
        properties: {
          scale: { type: 'number', minimum: 0 },
          x: { type: 'number' },
          y: { type: 'number' },
          rotate: { type: 'number' },
          opacity: { type: 'number', minimum: 0, maximum: 1 },
        },
      },
    },
    required: ['clip'],
  },
  idArgPaths: ['clip'],
  describeCall: (a) => `Set ${a.clip} ${describeProps(a, ['speed', 'volume', 'muted', 'fadeIn', 'fadeOut'])}`,
  async resolve(args) {
    return ok({ ...args });
  },
  apply(draft, a) {
    const err = requireClip(draft, a.clip, 'set_clip');
    if (err) return { error: err };
    const loc = findClip(draft, a.clip as string)!;
    const patch: Record<string, unknown> = {};
    for (const key of ['speed', 'volume', 'muted', 'fadeIn', 'fadeOut'] as const) {
      if (a[key] !== undefined) patch[key] = a[key];
    }
    if (a.transform && typeof a.transform === 'object') {
      const existing = (loc.clip as { transform?: Record<string, unknown> }).transform;
      if (!existing) {
        return { error: `${a.clip} has no transform (text and audio clips cannot be transformed)` };
      }
      patch.transform = { ...existing, ...(a.transform as Record<string, unknown>) };
    }
    return ops.updateClip(draft, { clip: a.clip as string, patch });
  },
};

// ---------------------------------------------------------------------------
// transitions
// ---------------------------------------------------------------------------

export const setTransitionTool: MutateToolDef = {
  name: 'set_transition',
  kind: 'mutate',
  description:
    'Put a transition on the START of a clip, crossing into the clip before it on the same track. The clip moves left to create the overlap.',
  schema: {
    type: 'object',
    properties: {
      clip: { type: 'string' },
      type: { type: 'string', enum: [...TRANSITION_TYPES] },
      duration: { type: 'number', minimum: 0.05, maximum: 10 },
    },
    required: ['clip', 'type', 'duration'],
  },
  idArgPaths: ['clip'],
  describeCall: (a) => `${a.type} ${fmtT(a.duration)} into ${a.clip}`,
  async resolve(args) {
    return ok({ clip: args.clip, type: args.type, duration: args.duration });
  },
  apply(draft, a) {
    const err = requireClip(draft, a.clip, 'set_transition');
    if (err) return { error: err };
    const loc = findClip(draft, a.clip as string)!;
    if (loc.track.kind !== 'video') {
      return { error: `${a.clip} is not on a video track — transitions apply to video clips only` };
    }
    const index = (loc.track.clips as { id: string }[]).findIndex((c) => c.id === a.clip);
    if (index <= 0) {
      return {
        error: `${a.clip} is the first clip on its track, so there is nothing to transition from. Put the transition on the following clip instead.`,
      };
    }
    return ops.setClipTransition(draft, {
      clip: a.clip as string,
      type: a.type as TransitionType,
      duration: a.duration as number,
    });
  },
};

export const removeTransitionTool: MutateToolDef = {
  name: 'remove_transition',
  kind: 'mutate',
  description: 'Remove the transition at the start of a clip, closing the overlap.',
  schema: {
    type: 'object',
    properties: { clip: { type: 'string' } },
    required: ['clip'],
  },
  idArgPaths: ['clip'],
  describeCall: (a) => `Remove transition on ${a.clip}`,
  async resolve(args) {
    return ok({ clip: args.clip });
  },
  apply(draft, a) {
    const err = requireClip(draft, a.clip, 'remove_transition');
    if (err) return { error: err };
    return ops.removeClipTransition(draft, { clip: a.clip as string });
  },
};

// ---------------------------------------------------------------------------
// effects
// ---------------------------------------------------------------------------

export const setEffectTool: MutateToolDef = {
  name: 'set_effect',
  kind: 'mutate',
  description:
    'Add or update a colour/blur effect on a clip. `amount` means: brightness -1..1, contrast/saturation 0..2, grayscale/sepia/invert 0..1, hue degrees -180..180, blur radius 0..50.',
  schema: {
    type: 'object',
    properties: {
      clip: { type: 'string' },
      type: { type: 'string', enum: [...EFFECT_TYPES] },
      amount: { type: 'number' },
    },
    required: ['clip', 'type', 'amount'],
  },
  idArgPaths: ['clip'],
  describeCall: (a) => `Set ${a.type} ${a.amount} on ${a.clip}`,
  async resolve(args) {
    return ok({ clip: args.clip, type: args.type, amount: args.amount });
  },
  apply(draft, a) {
    const err = requireClip(draft, a.clip, 'set_effect');
    if (err) return { error: err };
    const loc = findClip(draft, a.clip as string)!;
    const clip = loc.clip as { effects?: Effect[] };
    if (!clip.effects) {
      return { error: `${a.clip} cannot take effects (text and audio clips have none)` };
    }
    const type = a.type as EffectType;
    const paramKey = EFFECT_PARAM[type];
    const existing = clip.effects.find((e) => e.type === type);
    const effects = existing
      ? clip.effects.map((e) =>
          e.type === type ? ({ ...e, [paramKey]: a.amount } as Effect) : e,
        )
      : [
          ...clip.effects,
          { id: genEffectId(type), type, [paramKey]: a.amount } as unknown as Effect,
        ];
    return ops.updateClip(draft, { clip: a.clip as string, patch: { effects } });
  },
};

export const removeEffectTool: MutateToolDef = {
  name: 'remove_effect',
  kind: 'mutate',
  description: 'Remove an effect from a clip by its type.',
  schema: {
    type: 'object',
    properties: { clip: { type: 'string' }, type: { type: 'string', enum: [...EFFECT_TYPES] } },
    required: ['clip', 'type'],
  },
  idArgPaths: ['clip'],
  describeCall: (a) => `Remove ${a.type} from ${a.clip}`,
  async resolve(args) {
    return ok({ clip: args.clip, type: args.type });
  },
  apply(draft, a) {
    const err = requireClip(draft, a.clip, 'remove_effect');
    if (err) return { error: err };
    const loc = findClip(draft, a.clip as string)!;
    const clip = loc.clip as { effects?: Effect[] };
    if (!clip.effects?.some((e) => e.type === a.type)) {
      return { error: `${a.clip} has no ${a.type} effect` };
    }
    return ops.updateClip(draft, {
      clip: a.clip as string,
      patch: { effects: clip.effects.filter((e) => e.type !== a.type) },
    });
  },
};

// ---------------------------------------------------------------------------
// keyframes (bulk — one call, never N)
// ---------------------------------------------------------------------------

export const setKeyframesTool: MutateToolDef = {
  name: 'set_keyframes',
  kind: 'mutate',
  description:
    'Animate one parameter of a clip over time, replacing any existing animation. `param` is transform.scale | transform.x | transform.y | transform.rotate | transform.opacity, or effects.<type>.amount. Keyframe `t` is TIMELINE seconds; they are converted to the clip axis for you. Always set every keyframe in ONE call.',
  schema: {
    type: 'object',
    properties: {
      clip: { type: 'string' },
      param: { type: 'string' },
      keyframes: {
        type: 'array',
        maxItems: 40,
        items: {
          type: 'object',
          properties: {
            t: { type: 'number', description: 'timeline seconds' },
            value: { type: 'number' },
            easing: { type: 'string', enum: [...EASINGS] },
          },
          required: ['t', 'value'],
        },
      },
    },
    required: ['clip', 'param', 'keyframes'],
  },
  idArgPaths: ['clip'],
  describeCall: (a) =>
    `Animate ${a.param} on ${a.clip} (${(a.keyframes as unknown[])?.length ?? 0} keyframes)`,
  async resolve(args) {
    const kfs = args.keyframes as { t: number; value: number; easing?: Easing }[];
    if (kfs.length < 1) return fail('keyframes must not be empty');
    return ok({ clip: args.clip, param: args.param, keyframes: kfs });
  },
  apply(draft, a) {
    const err = requireClip(draft, a.clip, 'set_keyframes');
    if (err) return { error: err };
    const loc = findClip(draft, a.clip as string)!;
    const path = resolveParamPath(loc.clip, a.param as string);
    if (!path) {
      return {
        error: `"${a.param}" is not an animatable parameter of ${a.clip}. Use transform.scale/x/y/rotate/opacity, or effects.<type>.amount for an effect already on the clip.`,
      };
    }
    const input = a.keyframes as { t: number; value: number; easing?: Easing }[];
    // Timeline time → the clip's own keyframe axis (source time for media,
    // clip-local for overlays). Doing it here is what lets the model think
    // purely in timeline seconds.
    const converted: Keyframe[] = input.map((k) => ({
      t: clipParamTime(loc.clip as never, k.t),
      value: k.value,
      easing: k.easing ?? 'linear',
    }));
    converted.sort((x, y) => x.t - y.t);
    // Keyframes that collapse onto the same axis time (because they were
    // outside the clip and got clamped) would violate the strictly-sorted
    // invariant, so drop the duplicates rather than emit an invalid edit.
    const unique: Keyframe[] = [];
    for (const k of converted) {
      if (unique.length && Math.abs(k.t - unique[unique.length - 1].t) < 1e-6) {
        unique[unique.length - 1] = k;
      } else unique.push(k);
    }
    if (unique.length < 2) {
      return {
        error: `all keyframes fell on the same point of ${a.clip}; spread them across the clip's timeline span (see inspect_timeline)`,
      };
    }
    if (!setParamAtPath(loc.clip, path, { keyframes: unique })) {
      return { error: `could not set "${a.param}" on ${a.clip}` };
    }
    return { created: [] };
  },
};

/**
 * Map the model-facing param name onto a real param path. Effects are addressed
 * by TYPE in the tool ('effects.blur.amount') but by ID in the format
 * ('effects.<id>.radius'), because ids are meaningless to the model.
 */
function resolveParamPath(clip: object, param: string): string | null {
  if (param.startsWith('transform.')) {
    const key = param.slice('transform.'.length);
    const tr = (clip as { transform?: Record<string, unknown> }).transform;
    return tr && key in tr ? param : null;
  }
  if (param.startsWith('effects.')) {
    const rest = param.slice('effects.'.length);
    const type = rest.split('.')[0] as EffectType;
    const effects = (clip as { effects?: Effect[] }).effects;
    const effect = effects?.find((e) => e.type === type);
    if (!effect) return null;
    return `effects.${effect.id}.${EFFECT_PARAM[type]}`;
  }
  if (param === 'position.x' || param === 'position.y') {
    return (clip as { position?: unknown }).position ? param : null;
  }
  return null;
}

/**
 * Effect ids must be unique within a clip (repairEditIds enforces per-clip
 * uniqueness, and keyframe paths address 'effects.<id>.<param>'), and `apply`
 * must be deterministic so replaying the same log twice gives the same edit.
 * set_effect keeps at most one effect per type per clip, so the type IS a
 * unique, stable, replay-safe id.
 */
function genEffectId(type: EffectType): string {
  return `fx_${type}`;
}

// ---------------------------------------------------------------------------
// tracks and settings
// ---------------------------------------------------------------------------

export const addTrackTool: MutateToolDef = {
  name: 'add_track',
  kind: 'mutate',
  description: 'Add a track. Video tracks stack: later ones draw over earlier ones.',
  schema: {
    type: 'object',
    properties: { kind: { type: 'string', enum: ['video', 'overlay', 'audio'] } },
    required: ['kind'],
  },
  describeCall: (a) => `Add a ${a.kind} track`,
  async resolve(args) {
    return ok({ kind: args.kind });
  },
  apply(draft, a) {
    return ops.addTrack(draft, { kind: a.kind as 'video' | 'overlay' | 'audio' });
  },
};

export const setTrackTool: MutateToolDef = {
  name: 'set_track',
  kind: 'mutate',
  description: 'Set track properties: label, muted, solo, gain (audio), blend/opacity (video).',
  schema: {
    type: 'object',
    properties: {
      track: { type: 'string' },
      label: { type: 'string' },
      muted: { type: 'boolean' },
      solo: { type: 'boolean' },
      gain: { type: 'number', minimum: 0, maximum: 2 },
      opacity: { type: 'number', minimum: 0, maximum: 1 },
      blend: { type: 'string', enum: [...TRACK_BLENDS] },
    },
    required: ['track'],
  },
  idArgPaths: ['track'],
  describeCall: (a) => `Set track ${a.track} ${describeProps(a, ['label', 'muted', 'solo', 'gain', 'opacity', 'blend'])}`,
  async resolve(args) {
    return ok({ ...args });
  },
  apply(draft, a) {
    const track = draft.tracks.find((t) => t.id === a.track);
    if (!track) return { error: `no track "${String(a.track)}" — use inspect_timeline` };
    const id = a.track as string;
    if (a.label !== undefined) ops.renameTrack(draft, { track: id, label: a.label as string });
    if (a.muted !== undefined) ops.setTrackMuted(draft, { track: id, muted: a.muted as boolean });
    if (a.solo !== undefined) ops.setTrackSolo(draft, { track: id, solo: a.solo as boolean });
    if (a.gain !== undefined) ops.setTrackGain(draft, { track: id, gain: a.gain as number });
    if (a.opacity !== undefined)
      ops.setTrackOpacity(draft, { track: id, opacity: a.opacity as number });
    if (a.blend !== undefined)
      ops.setTrackBlend(draft, { track: id, blend: a.blend as never });
    return { created: [] };
  },
};

export const setSettingsTool: MutateToolDef = {
  name: 'set_settings',
  kind: 'mutate',
  description: 'Set project resolution, frame rate or background colour.',
  schema: {
    type: 'object',
    properties: {
      width: { type: 'integer', minimum: 16, maximum: 7680 },
      height: { type: 'integer', minimum: 16, maximum: 4320 },
      fps: { type: 'number', minimum: 1, maximum: 120 },
      background: { type: 'string' },
    },
  },
  describeCall: (a) => `Set project ${describeProps(a, ['width', 'height', 'fps', 'background'])}`,
  async resolve(args) {
    if (!Object.keys(args).length) return fail('set_settings needs at least one property');
    return ok({ ...args });
  },
  apply(draft, a) {
    const patch: Record<string, unknown> = {};
    for (const key of ['width', 'height', 'fps', 'background'] as const) {
      if (a[key] !== undefined) patch[key] = a[key];
    }
    return ops.setSettings(draft, { patch });
  },
};

export const MUTATE_TOOLS: MutateToolDef[] = [
  addClipTool,
  addSequenceTool,
  addAudioTool,
  addTextTool,
  trimClipTool,
  moveClipTool,
  splitClipTool,
  detachAudioTool,
  deleteClipsTool,
  setClipTool,
  setTransitionTool,
  removeTransitionTool,
  setEffectTool,
  removeEffectTool,
  setKeyframesTool,
  addTrackTool,
  setTrackTool,
  setSettingsTool,
];

// ---------------------------------------------------------------------------
// describeCall helpers (proposal card op list — read by humans)
// ---------------------------------------------------------------------------

/**
 * The item id, from EITHER argument shape. describeCall is used in two places
 * with different args: the live activity chip sees the model's arguments
 * ({archive, item}), while the proposal card sees the RESOLVED op args, where
 * the item lives inside the embedded source. Reading only one shape renders
 * "Add clip undefined" on the card.
 */
function itemOf(args: Record<string, unknown>): string {
  if (typeof args.item === 'string') return args.item;
  const source = args.source as { item?: unknown } | undefined;
  return typeof source?.item === 'string' ? source.item : '?';
}

function fmtT(v: unknown): string {
  return typeof v === 'number' ? `${Math.round(v * 10) / 10}s` : '?';
}

function fmtRange(a: unknown, b: unknown): string {
  if (typeof a !== 'number' && typeof b !== 'number') return '';
  return `${typeof a === 'number' ? Math.round(a * 10) / 10 : '…'}–${
    typeof b === 'number' ? Math.round(b * 10) / 10 : '…'
  }s`;
}

function describeProps(args: Record<string, unknown>, keys: string[]): string {
  const parts = keys
    .filter((k) => args[k] !== undefined)
    .map((k) => `${k}=${JSON.stringify(args[k])}`);
  return parts.length ? parts.join(' ') : '(no change)';
}

export type { ApplyResult };
